import { TextBox } from 'devextreme-react';
import React, { useEffect, useState } from 'react';
import { APP_URLS } from '../../appUrls';
import SHAPES_PATTERN from '../../assets/images/login-shapes-pattern.svg';
import POINT_LOGO from '../../assets/images/point-logo.svg';
import appInsights from "../../common/applicationInsights";
import { StorageKeys } from '../../common/storageKeys';
import EventProcess from "../../common/trackedEventProcessEnum";
import EventTypes from "../../common/trackedEventTypeEnum";
import Button from '../../components/button/button';
import Icon from '../../components/icon/icon';
import IconEnum from '../../components/icon/iconEnum';
import OuterLink from '../../components/outerLink/outerLink';
import { ApiException, ValidationsClient } from '../../services/service';

export const PointLogin: React.FC = () => {
    const [domain, setDomain] = useState<string>(() => {
        const savedDomain = localStorage.getItem(StorageKeys.PointRedirectDomain);
        if (savedDomain && savedDomain.trim().length > 0) {
            return savedDomain;
        }
        return '';
    });
    const [showDomainHelp, setShowDomainHelp] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<{ title: string; message: string } | null>(null);

    useEffect(() => {
        appInsights.trackEvent("User opened the point login screen", EventTypes.OpenedPortal, EventProcess.Default);
    }, []);

    const handleContinue = async () => {
        if (domain && domain.trim().length > 0) {
            setIsLoading(true);
            setError(null);

            const validationsClient = new ValidationsClient();
            validationsClient.isAnonymous = true;
            const trimmedDomain = domain.trim();

            try {
                const response = await validationsClient.validateActiveSubdomain({ subdomain: trimmedDomain });
                localStorage.setItem(StorageKeys.PointRedirectDomain, trimmedDomain);
                const fullUrl = response?.url ?? `https://${trimmedDomain}.syskit365.com`;
                window.location.href = fullUrl;
            } catch (err) {
                if (ApiException.isApiException(err) && err.status === 404) {
                    setError({ title: 'Domain not found', message: 'Please check your domain name and try again.' });
                } else {
                    console.error('Error checking domain:', err);
                    setError({ title: 'Something went wrong', message: err.message || 'Failed to verify domain. Please try again.' });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const toggleDomainHelp = () => {
        setShowDomainHelp(!showDomainHelp);
    };

    return (
        <div className="tw-relative tw-z-0 tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-overflow-auto tw-bg-white sm:tw-overflow-hidden sm:tw-bg-space-cadet/6">
            <div className="tw-relative tw-flex tw-w-[480px] tw-max-w-full tw-flex-col tw-items-center tw-justify-center tw-rounded-[57px] tw-bg-white tw-p-12 tw-text-center sm:tw-max-w-xl">
                <img
                    src={SHAPES_PATTERN}
                    className="tw-absolute tw-right-[calc(100%-30px)] -tw-z-10 tw-hidden tw-h-[280px] sm:tw-block"
                    alt="Background pattern"
                />

                <img src={POINT_LOGO} className="tw-mb-8 tw-h-8" alt="Syskit Point" />

                <div className="tw-w-full tw-text-left">
                    <label className="tw-mb-2 tw-block tw-text-sm tw-font-medium tw-text-space-cadet">
                        Your Domain
                    </label>

                    <div className="tw-flex tw-items-center tw-gap-2">
                        <TextBox
                            value={domain}
                            onValueChanged={(e) => setDomain(e.value)}
                            placeholder="yourcompany"
                            stylingMode="outlined"
                            className="tw-flex-1"
                        />
                        <span className="tw-text-space-cadet">.syskit365.com</span>
                    </div>

                    {error && (
                        <div className="tw-mt-4 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-p-4 tw-text-left" style={{ borderColor: 'var(--flame)', backgroundColor: 'var(--melon)', color: 'var(--flame)' }}>
                            <div className="tw-text-sm tw-font-semibold">{error.title}</div>
                            <div className="tw-mt-1 tw-text-sm">{error.message}</div>
                        </div>
                    )}

                    <button
                        onClick={toggleDomainHelp}
                        className="tw-mt-5 tw-flex tw-cursor-pointer tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-text-cyber-grape"
                    >
                        What's my domain?
                        <Icon iconName={IconEnum.ArrowChevron} className={`tw-transition-transform tw-duration-300 ${showDomainHelp ? 'tw-rotate-90' : '-tw-rotate-90'}`} width={8} />
                    </button>

                    <div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-in-out ${showDomainHelp ? 'tw-grid-rows-[1fr] tw-opacity-100' : 'tw-grid-rows-[0fr] tw-opacity-0'}`}>
                        <div className="tw-overflow-hidden">
                            <div className="tw-mb-2 tw-mt-4 tw-rounded-[32px] tw-border tw-border-neutral-shadow tw-bg-casper/30 tw-p-4 tw-text-left">
                                <div className="tw-mb-2 tw-text-sm tw-font-semibold tw-leading-5 tw-text-space-cadet">
                                    How to find your domain:
                                </div>

                                <div className="tw-mb-2 tw-rounded-lg tw-bg-white tw-p-4">
                                    <div className="tw-mb-2 tw-text-xs tw-text-stardust">
                                        Address bar example:
                                    </div>
                                    <div className="tw-text-sm tw-text-space-cadet">
                                        https://<span className="tw-mx-2 tw-rounded tw-bg-cyber-grape/10 tw-px-2 tw-font-semibold tw-text-cyber-grape">yourcompany</span>.syskit365.com
                                    </div>
                                    <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-cyber-grape">
                                        <Icon iconName={IconEnum.Arrow} height={12} className="tw-rotate-90" style={{ fill: '#6B46C1' }} />
                                        <span className="tw-font-medium">That's your domain!</span>
                                    </div>
                                </div>

                                <div className="tw-text-sm tw-leading-relaxed tw-text-space-cadet">
                                    Take a look at the address bar when you are logged in to Syskit Point.
                                    The text just before .syskit365.com is your domain.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <Button
                    onClick={handleContinue}
                    disabled={!domain || domain.trim().length === 0 || isLoading}
                    loading={isLoading}
                    className="tw-mt-5 tw-w-full tw-rounded-[32px] tw-px-16 tw-py-4 tw-shadow-md"
                >
                    Continue
                </Button>

                <div className="tw-mt-8 tw-flex tw-gap-4 tw-text-sm tw-font-normal tw-text-stardust">
                    <OuterLink href={APP_URLS.privacyPolicy} className="tw-text-stardust hover:tw-underline">
                        Privacy Policy
                    </OuterLink>
                    <span>•</span>
                    <OuterLink href={APP_URLS.termsOfUse} className="tw-text-stardust hover:tw-underline">
                        Terms of Service
                    </OuterLink>
                </div>
            </div>

            <div className="tw-mt-6 tw-text-sm tw-text-space-cadet">
                Don't have an account?
                <OuterLink href={APP_URLS.contactSupport} className="tw-ml-1 tw-text-cyber-grape hover:tw-underline">
                    Contact us
                </OuterLink>
            </div>
        </div>
    );
}